import { Injectable, inject } from '@angular/core'
import { Observable, catchError, of, tap, finalize, map } from 'rxjs'
import { BaseAsyncService } from '@core/state/base-service'
import { DemandeDocumentsStore } from '@core/state/demande-documents.store'
import {
  DemandeDocumentCreateRequest,
  DemandeDocumentResponse,
  DemandeDocumentResponseApiOneSuccess,
  DemandeDocumentResponseApiListSuccess,
  Role,
} from '@shared/models/dto/api-dtos'

@Injectable({ providedIn: 'root' })
export class DemandeDocumentsService extends BaseAsyncService {
  private readonly store = inject(DemandeDocumentsStore)

  /**
   * Creates a new document request
   * @param request DemandeDocumentCreateRequest
   * @returns Observable of the created request
   */
  createRequest(request: DemandeDocumentCreateRequest): Observable<DemandeDocumentResponse | null> {
    this.store.setLoading(true)
    this.store.setError(null)

    return this.post<DemandeDocumentResponseApiOneSuccess>('/demandes-documents', request, {
      logErrors: true,
      retry: this.createRetryConfig(2, 800, 1.5),
    }).pipe(
      map(response => response?.data ?? null),
      tap(response => {
        if (response) {
          this.store.addRequest(response)
        }
      }),
      catchError(error => {
        console.error('Error creating document request', error)
        // Don't set store error for create operations - component will show toast
        return of(null)
      }),
      finalize(() => this.store.setLoading(false))
    )
  }

  /**
   * Loads document requests for a dossier filtered by recipient role
   * @param dossierId The dossier ID
   * @param recipientRole The recipient role (Role enum)
   * @returns Observable of array of requests
   */
  loadRequests(dossierId: string, recipientRole: Role): Observable<DemandeDocumentResponse[]> {
    this.store.setLoading(true)
    this.store.setError(null)

    const endpoint = `/demandes-documents/dossier/${dossierId}/role/${recipientRole}`

    return this.get<DemandeDocumentResponseApiListSuccess>(endpoint, {
      logErrors: true,
      retry: this.createRetryConfig(2, 800, 1.5),
    }).pipe(
      map(response => response?.data ?? []),
      tap(requests => {
        this.store.setRequests(requests, dossierId, recipientRole)
      }),
      catchError(error => {
        console.error('Error loading document requests', error)
        this.store.setError('Impossible de charger les demandes de documents')
        return of([])
      }),
      finalize(() => this.store.setLoading(false))
    )
  }

  /**
   * Deletes a document request
   * Note: This assumes there's a DELETE endpoint. If not, this method can be removed or updated.
   * @param requestId The request ID to delete
   * @returns Observable of success
   */
  deleteRequest(requestId: number): Observable<boolean> {
    this.store.setLoading(true)
    this.store.setError(null)

    const endpoint = `/demandes-documents/${requestId}`

    return this.delete<{ success: boolean }>(endpoint, {
      logErrors: true,
      retry: this.createRetryConfig(2, 800, 1.5),
    }).pipe(
      tap(() => {
        this.store.removeRequest(requestId)
      }),
      catchError(error => {
        console.error('Error deleting document request', error)
        // Don't set store error for delete operations - component will show toast
        return of(false)
      }),
      finalize(() => this.store.setLoading(false)),
      map(() => true)
    )
  }
}
